const HEIC_BRANDS = new Set([
    'heic',
    'heix',
    'hevc',
    'hevx',
    'heim',
    'heis',
    'mif1',
    'msf1'
]);

const MAX_IMAGE_SIDE = 4096;

function fileExtension(name: string) {
    const base = name.split(/[/\\]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
}

function declaredType(file: File) {
    return file.type.toLowerCase().split(';')[0].trim();
}

function isHeicLabel(file: File) {
    const type = declaredType(file);
    const extension = fileExtension(file.name);
    return type === 'image/heic' || type === 'image/heif' || extension === 'heic' || extension === 'heif';
}

function jpegName(name: string) {
    const base = name.split(/[/\\]/).pop() ?? 'bild';
    const dot = base.lastIndexOf('.');
    const stem = dot > 0 ? base.slice(0, dot) : base;
    return `${stem || 'bild'}.jpg`;
}

async function sniffImage(file: File): Promise<'jpeg' | 'png' | 'heic' | null> {
    const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        return 'jpeg';
    }
    if (
        bytes.length >= 8
        && bytes[0] === 0x89
        && bytes[1] === 0x50
        && bytes[2] === 0x4e
        && bytes[3] === 0x47
    ) {
        return 'png';
    }
    if (bytes.length < 12) {
        return null;
    }
    const box = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
    if (box !== 'ftyp') {
        return null;
    }
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]).toLowerCase();
    return HEIC_BRANDS.has(brand) ? 'heic' : null;
}

function loadImage(url: string) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Bilden kunde inte läsas. Spara den som JPG och försök igen.'));
        image.src = url;
    });
}

function canvasToJpeg(canvas: HTMLCanvasElement) {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Bilden kunde inte göras om till JPG. Spara den som JPG och försök igen.'));
                return;
            }
            resolve(blob);
        }, 'image/jpeg', 0.85);
    });
}

async function convertToJpeg(file: File) {
    const url = URL.createObjectURL(file);
    try {
        const image = await loadImage(url);
        const longest = Math.max(image.naturalWidth, image.naturalHeight);
        if (!longest) {
            throw new Error('Bilden kunde inte läsas. Spara den som JPG och försök igen.');
        }
        const scale = Math.min(1, MAX_IMAGE_SIDE / longest);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.naturalWidth * scale);
        canvas.height = Math.round(image.naturalHeight * scale);
        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Bilden kunde inte göras om till JPG. Spara den som JPG och försök igen.');
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await canvasToJpeg(canvas);
        return new File([blob], jpegName(file.name), {
            type: 'image/jpeg',
            lastModified: file.lastModified
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function withType(file: File, type: string, name = file.name) {
    if (file.type === type && file.name === name) {
        return file;
    }
    return new File([file], name, { type, lastModified: file.lastModified });
}

/** Turns a phone HEIC photo into a JPEG in the browser. Other files pass through. */
export async function prepareOrderFile(file: File) {
    const kind = await sniffImage(file);
    if (kind === 'heic' || (kind === null && isHeicLabel(file))) {
        return convertToJpeg(file);
    }
    if (kind === 'jpeg') {
        return withType(file, 'image/jpeg', fileExtension(file.name) === 'jpeg' ? file.name : jpegName(file.name));
    }
    if (kind === 'png') {
        return withType(file, 'image/png');
    }
    return file;
}
